/**
 * QuoteManagement.jsx — 견적서 관리 메인 컴포넌트
 *
 * 분리된 모듈:
 *   constants.js             — 상수·스타일·유틸
 *   QuotePrintView.jsx       — 인쇄/PDF 레이아웃
 *   QuoteFormComponents.jsx  — 자동완성·편집기·포털·모바일훅
 *   QuoteFormModal.jsx       — 등록/수정 모달 (바텀시트/센터 모달)
 *   useQuoteManagement.js    — 상태·이펙트·핸들러 훅
 */
import React from 'react';
import { Search } from 'lucide-react';
import { toDateString } from '../../../utils/date';
import { useBodyScrollLock } from '../../../hooks/useBodyScrollLock';
import QuotePrintView from './QuotePrintView';
import { PrintPortal } from './QuoteFormComponents';
import QuoteFormModal from './QuoteFormModal';
import { useQuoteManagement } from './useQuoteManagement';
import { parseFreeItems, btnDanger } from './constants';
import {
  stdPageTitle, stdPageSubtitle, stdHeaderRow, stdBlackBtn, stdOutlineBtn,
  stdTableWrap, stdTable, stdTh, stdCell,
  stdSearchWrap, stdSearchInput, stdSearchIcon,
} from '../adminlayout/designStandard';

export default function QuoteManagement() {
  const {
    isMobile,
    filtered, loading, search, setSearch, calcTotal,
    showForm, setShowForm, editId, form, freeRows, setFreeRows,
    saving, formErr, salesPersons, promotionalItems,
    stamp, stampStatus, stampUploading, stampInputRef, reloadStamp,
    printQuote, logoB64,
    pdfRef, pdfQuote, pdfSaving,
    sheetDragY, sheetDragging, sheetHandleRef,
    openNew, openEdit, setField,
    handleSave, handleDelete, handleStampUpload,
    handlePrint, handleSavePdf,
  } = useQuoteManagement();
  useBodyScrollLock(pdfSaving);

  return (
    <div>

      {/* 브라우저 인쇄 CSS — #quote-print-portal만 표시 */}
      <style>{`
        @media print {
          body > * { display: none !important; }
          #quote-print-portal { display: block !important; }
          @page { margin: 5mm 8mm; size: A4 portrait; }
        }
      `}</style>

      {/* 브라우저 인쇄 포털 */}
      <PrintPortal quote={printQuote} stamp={stamp} logo={logoB64} />

      {/* PDF 저장용 렌더 엘리먼트 (viewport 안에 있어야 html2canvas 캡처 가능) */}
      {pdfQuote && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: 794, background: 'var(--bg-card)', zIndex: 8000, pointerEvents: 'none', boxSizing: 'border-box' }}>
          <div ref={pdfRef}>
            <QuotePrintView quote={pdfQuote} stamp={stamp} logo={logoB64} />
          </div>
        </div>
      )}

      {/* PDF 생성 중 로딩 오버레이 */}
      {pdfSaving && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'var(--bg-card)', borderRadius: 14, padding: '28px 40px', textAlign: 'center', boxShadow: '0 8px 40px rgba(0,0,0,0.25)' }}>
            <div style={{ fontSize: 28, marginBottom: 12 }}>📄</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-sub)', marginBottom: 6 }}>PDF 생성 중...</div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>잠시만 기다려 주세요.</div>
          </div>
        </div>
      )}

      {/* ── 페이지 헤더 — 디자인 표준(직원 관리 기준): 텍스트 헤더 + 우측 액션 ── */}
      <div style={stdHeaderRow(isMobile)}>
        <div>
          <h2 style={stdPageTitle}>견적서 관리</h2>
          <div style={stdPageSubtitle}>견적서를 작성·관리합니다.</div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <button
            style={stdOutlineBtn}
            onClick={() => stampInputRef.current?.click()}
            disabled={stampUploading || stampStatus === 'loading' || (stampStatus === 'error' && !stamp)}
          >
            {stampStatus === 'loading' ? '🔏 도장 불러오는 중…'
              : stamp ? '🔏 도장 변경'
              : stampStatus === 'error' ? '🔏 도장 불러오기 실패'
              : '🔏 도장 업로드'}
          </button>
          <input ref={stampInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleStampUpload} />
          <button style={stdBlackBtn} onClick={openNew}>+ 견적서 등록</button>
        </div>
      </div>

      {stamp && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '0 0 16px' }}>
          <img src={stamp} alt="도장" style={{ width: 40, height: 40, objectFit: 'contain', border: '1px solid var(--border)', borderRadius: 6 }} />
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>현재 도장 이미지 — 인쇄/PDF 시 공급자 영역에 표시됩니다.</span>
        </div>
      )}
      {stampStatus === 'error' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '0 0 16px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, color: '#b45309' }}>
            도장 정보를 불러오지 못했습니다. {stamp ? '(현재 보이는 도장은 마지막 저장본입니다) ' : ''}네트워크 확인 후 다시 시도해 주세요.
          </span>
          <button style={stdOutlineBtn} onClick={() => reloadStamp()}>다시 불러오기</button>
        </div>
      )}

      {/* ── 검색 — 디자인 표준 검색창 ── */}
      <div style={stdSearchWrap(isMobile)}>
        <Search size={15} style={stdSearchIcon} />
        <input
          style={stdSearchInput}
          placeholder="문서번호, 공급받는 자, 요청자, 품목 검색"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* 건수 표시 */}
      <div style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600, marginBottom: 8 }}>{filtered.length}건</div>

      {loading ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>로딩 중...</div>
      ) : filtered.length === 0 ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>{search ? '검색 결과가 없습니다.' : '등록된 견적서가 없습니다.'}</div>
      ) : isMobile ? (
        /* 모바일: 카드형 목록 */
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtered.map((q) => (
            <div key={q.id} style={{ border: '1px solid var(--border)', borderRadius: 10, padding: '12px 14px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                <div>
                  <span style={{ fontWeight: 800, color: '#1d4ed8', fontSize: 15 }}>{q.quote_number}</span>
                  <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 8 }}>{q.issue_date ? toDateString(q.issue_date) : ''}</span>
                </div>
                <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--text)' }}>{calcTotal(q)}원</span>
              </div>
              <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 2 }}>{q.recipient_name}</div>
              {q.product1_name && (
                <div style={{ fontSize: 12, color: 'var(--text-sub)' }}>
                  {q.product1_name}
                  {q.product1_qty != null && <span style={{ color: 'var(--text-muted)', marginLeft: 4 }}>{Number(q.product1_qty).toLocaleString('ko-KR')}장</span>}
                </div>
              )}
              {q.product2_name && (
                <div style={{ fontSize: 12, color: '#7c3aed', marginTop: 2 }}>
                  {q.product2_name} <span style={{ fontSize: 11, fontWeight: 600 }}>서비스</span>
                  {q.product2_qty != null && <span style={{ color: '#a78bfa', marginLeft: 4 }}>{Number(q.product2_qty).toLocaleString('ko-KR')}장</span>}
                </div>
              )}
              {q.requester_name && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>요청자: {q.requester_name}</div>}
              <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
                <button style={{ ...stdOutlineBtn, flex: 1, padding: '7px 0', textAlign: 'center' }} onClick={() => handlePrint(q)}>🖨️ 인쇄</button>
                <button style={{ ...stdBlackBtn, flex: 1, padding: '7px 0', textAlign: 'center' }} onClick={() => handleSavePdf(q)} disabled={pdfSaving}>📄 PDF</button>
                <button style={{ ...stdOutlineBtn, flex: 1, padding: '7px 0', textAlign: 'center' }} onClick={() => openEdit(q)}>수정</button>
                <button style={{ ...btnDanger, flex: 1, fontSize: 12, padding: '7px 0', textAlign: 'center' }} onClick={() => handleDelete(q.id)}>삭제</button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* 데스크탑: 테이블형 목록 — 디자인 표준 표 */
        <div style={stdTableWrap}>
          <table style={stdTable}>
            <thead>
              <tr>
                {['문서번호','발행일','공급받는 자','요청자','품목','장수','서비스 장수','무상제공품목','합계금액','작업'].map((h) => (
                  <th key={h} style={stdTh}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((q, rowIndex) => {
                const cell = stdCell(rowIndex, filtered.length);
                return (
                  <tr key={q.id}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-subtle)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = ''; }}
                  >
                    <td style={{ ...cell, fontWeight: 800, color: '#1d4ed8' }}>{q.quote_number}</td>
                    <td style={{ ...cell, whiteSpace: 'nowrap', color: 'var(--text-sub)' }}>{q.issue_date ? toDateString(q.issue_date) : '-'}</td>
                    <td style={{ ...cell, fontWeight: 600 }}>{q.recipient_name}</td>
                    <td style={{ ...cell, color: 'var(--text-muted)' }}>{q.requester_name || '-'}</td>
                    <td style={cell}>
                      {q.product1_name || '-'}
                      {q.product2_name && <span style={{ color: '#7c3aed', marginLeft: 4, fontSize: 11, fontWeight: 600 }}>+서비스</span>}
                    </td>
                    <td style={{ ...cell, textAlign: 'right', color: 'var(--text-sub)' }}>
                      {q.product1_qty ? Number(q.product1_qty).toLocaleString('ko-KR') : '-'}
                    </td>
                    <td style={{ ...cell, textAlign: 'right', color: '#7c3aed' }}>
                      {q.product2_qty ? Number(q.product2_qty).toLocaleString('ko-KR') : '-'}
                    </td>
                    <td style={{ ...cell, color: '#059669', fontSize: 12 }}>
                      {parseFreeItems(q.free_items).length > 0
                        ? parseFreeItems(q.free_items).map((fi, i) => (
                            <span key={i} style={{ display: 'block', whiteSpace: 'nowrap' }}>{fi.name} {fi.qty}장</span>
                          ))
                        : <span style={{ color: 'var(--border)' }}>-</span>}
                    </td>
                    <td style={{ ...cell, fontWeight: 700, textAlign: 'right' }}>
                      {calcTotal(q)}{!q.vat_yn && <span style={{ fontSize: 10, color: 'var(--text-muted)', marginLeft: 4 }}>VAT제외</span>}
                    </td>
                    <td style={cell}>
                      <div style={{ display: 'flex', gap: 5 }}>
                        <button style={{ ...stdOutlineBtn, padding: '5px 10px' }} onClick={() => handlePrint(q)}>🖨️ 인쇄</button>
                        <button style={{ ...stdBlackBtn, padding: '5px 10px' }} onClick={() => handleSavePdf(q)} disabled={pdfSaving}>📄 PDF</button>
                        <button style={{ ...stdOutlineBtn, padding: '5px 10px' }} onClick={() => openEdit(q)}>수정</button>
                        <button style={{ ...btnDanger, padding: '5px 10px', fontSize: 12 }} onClick={() => handleDelete(q.id)}>삭제</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── 등록/수정 모달 ── */}
      <QuoteFormModal
        isMobile={isMobile}
        showForm={showForm} setShowForm={setShowForm}
        sheetDragY={sheetDragY} sheetDragging={sheetDragging} sheetHandleRef={sheetHandleRef}
        form={form} setField={setField} editId={editId}
        salesPersons={salesPersons} freeRows={freeRows} setFreeRows={setFreeRows}
        promotionalItems={promotionalItems}
        saving={saving} handleSave={handleSave} formErr={formErr}
      />
    </div>
  );
}
